<template>
  <div class="barcode-card">

    <!-- Corner decorations -->
    <div class="deco deco--tl" aria-hidden="true">
      <svg viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="40" r="70" fill="#78BE95" opacity="0.18"/>
        <circle cx="10" cy="10" r="28" fill="#F0D25B" opacity="0.20"/>
      </svg>
    </div>
    <div class="deco deco--br" aria-hidden="true">
      <svg viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="120" cy="120" r="65" fill="#4489C6" opacity="0.13"/>
        <circle cx="148" cy="148" r="28" fill="#D6ACCC" opacity="0.18"/>
      </svg>
    </div>

    <div class="card-content">

      <!-- Abby Greeting Header -->
      <div class="abby-header">
        <div class="abby-mascot">
          <img src="@/assets/pcu/2026/abby-smile-half.png" alt="Abby Mascot" class="abby-img" />
        </div>
        <div class="abby-dialog">
          <p class="greeting-title">Selamat, pendaftaran selesai! 🎉🎊</p>
          <p class="greeting-body">
            Terima kasih <strong v-if="nickname">{{ nickname }}</strong><strong v-else>ya</strong>!
            Kamu sudah berhasil menyelesaikan seluruh proses pendaftaran
            <strong>Petra Parade 2026</strong>. Kami sangat senang bisa membantu perjalananmu!
            Sampai jumpa di hari H! 🚀✨
          </p>
        </div>
      </div>

      <!-- View toggle tabs -->
      <div class="view-tabs">
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': !showSummary }"
          @click="showSummary = false"
        >
          🎫 QR Absen
        </button>
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': showSummary }"
          @click="showSummary = true"
        >
          📋 Ringkasan Hasil
        </button>
      </div>

      <!-- ===== QR CODE VIEW ===== -->
      <div class="qr-section" v-show="!showSummary">
        <div class="qr-wrapper">
          <div v-if="isGenerating" class="qr-loading">
            <div class="qr-spinner"></div>
            <p>Memuat QR Code...</p>
          </div>
          <canvas v-show="!isGenerating" ref="canvasRef" class="qr-canvas"></canvas>
          <p v-show="!isGenerating" class="qr-code-number">{{ barcodeCode }}</p>
        </div>

        <!-- Instructions -->
        <div class="instructions-box">
          <p class="instructions-title">📌 Cara menggunakan QR Code ini:</p>
          <ol class="instructions-list">
            <li>📸 <strong>Screenshot</strong> halaman ini atau klik tombol <strong>Download</strong> di bawah.</li>
            <li>📱 Simpan gambar QR Code di galeri HP kamu.</li>
            <li>✅ Tunjukkan QR Code ini kepada <strong>panitia Petra Parade 2026</strong> saat check-in di venue untuk <strong>absensi kehadiran</strong>.</li>
          </ol>
        </div>

        <!-- Download / Preview Buttons -->
        <div class="btn-row">
          <button class="btn-preview" @click="previewQR" hidden="true" style="display: none;">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" class="btn-icon">
              <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"/>
            </svg>
            Preview
          </button>
          <button class="btn-download" @click="downloadQR">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="btn-icon">
              <path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 242.7-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7 288 32zM64 352c-35.3 0-64 28.7-64 64l0 32c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-32c0-35.3-28.7-64-64-64l-101.5 0-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352 64 352zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"/>
            </svg>
            Download
          </button>
        </div>

        <p class="event-closing">
          <strong>Petra Parade 2026</strong> — See you there! 🎓
        </p>
      </div>

      <!-- ===== SUMMARY VIEW ===== -->
      <div class="summary-section" v-show="showSummary">

        <!-- Pilihan jurusan -->
        <div v-if="(selectedProdis || []).length > 0 || (extraProdis || []).length > 0" class="summary-block">
          <h3 class="summary-block-title">PILIHAN JURUSANMU</h3>
          <div class="chips-wrap">
            <span v-for="id in (selectedProdis || [])" :key="id" class="chip chip--petra">
              🎓 {{ (prodiList || {})[id] || id }}
            </span>
            <span v-for="(item, i) in (extraProdis || [])" :key="'extra-' + i" class="chip chip--custom">
              ✏️ {{ (prodiList || {})[item] || item }}
            </span>
          </div>
        </div>

        <!-- Priority program dari hasil tes -->
        <div v-if="results?.priority_program" class="result-card result-card--priority">
          <h3 class="result-card-label">Rekomendasi Jurusan Sesuai Hasil Tes Kamu</h3>
          <div class="priority-row">
            <div class="priority-icon-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="priority-star">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
            </div>
            <span class="priority-name">{{ results.priority_program }}</span>
            <!-- <span class="priority-badge">Top Match</span> -->
          </div>
          <div v-if="results.priority_program2" class="priority-row">
            <div class="priority-icon-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="priority-star">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
            </div>
            <span class="priority-name">{{ results.priority_program2 }}</span>
            <!-- <span class="priority-badge">Top Match</span> -->
          </div>
        </div>

        <!-- Program yang dipilih untuk dikunjungi -->
        <div v-if="chosenProdiName" class="result-card result-card--chosen">
          <h3 class="result-card-label">Program yang Akan Kamu Kunjungi</h3>
          <div class="chosen-row">
            <div class="chosen-icon-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="chosen-icon">
                <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"/>
              </svg>
            </div>
            <span class="chosen-name">{{ chosenProdiName }}</span>
            <span class="chosen-badge">Pilihanmu ✓</span>
          </div>
        </div>

        <!-- Tombol kembali ke QR -->
        <button class="btn-back-qr" @click="showSummary = false">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="btn-icon">
            <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
          </svg>
          Kembali ke QR Code
        </button>

      </div>

      <!-- ===== PREVIEW MODAL ===== -->
      <Teleport to="body">
        <Transition name="modal-fade">
          <div v-if="showPreview" class="preview-overlay" @click.self="showPreview = false">
            <div class="preview-modal">
              <div class="preview-header">
                <span class="preview-title">Preview Kartu</span>
                <button class="preview-close" @click="showPreview = false" aria-label="Tutup">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="close-icon">
                    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
                  </svg>
                </button>
              </div>
              <div class="preview-body">
                <div v-if="isPreviewLoading" class="preview-loading">
                  <div class="qr-spinner"></div>
                  <p>Membuat preview...</p>
                </div>
                <img v-else :src="previewDataUrl" class="preview-img" alt="Preview kartu" />
              </div>
              <div class="preview-footer">
                <button class="preview-btn-download" @click="downloadFromPreview">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="btn-icon">
                    <path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 242.7-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7 288 32zM64 352c-35.3 0-64 28.7-64 64l0 32c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-32c0-35.3-28.7-64-64-64l-101.5 0-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352 64 352zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"/>
                  </svg>
                  Download Sekarang
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import QRCode from 'qrcode';
import logoUrl from '@/assets/pcu/2026/main-logo.png';

const props = defineProps({
  nickname: { type: String, default: '' },
  fullName: { type: String, default: '' },
  barcodeCode: { type: String, required: true },
  results: { type: Object, default: () => ({}) },
  selectedProdis: { type: Array, default: () => [] },
  extraProdis: { type: Array, default: () => [] },
  prodiList: { type: Object, default: () => ({}) },
  chosenProdiName: { type: String, default: '' }
});

const canvasRef = ref(null);
const isGenerating = ref(true);
const showSummary = ref(false);
const showPreview = ref(false);
const previewDataUrl = ref('');
const isPreviewLoading = ref(false);
let _lastCardDataUrl = '';

async function generateQR() {
  if (!canvasRef.value || !props.barcodeCode) return;
  isGenerating.value = true;
  try {
    await QRCode.toCanvas(canvasRef.value, props.barcodeCode, {
      width: 220,
      margin: 2,
      color: {
        dark: '#26488A',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'H'
    });
  } catch (err) {
    console.error('QR generation error:', err);
  } finally {
    isGenerating.value = false;
  }
}

onMounted(generateQR);
watch(() => props.barcodeCode, generateQR);

async function buildCard() {
  const srcCanvas = canvasRef.value;
  if (!srcCanvas) return null;

  // iOS Safari has strict canvas memory limits (~16MB total area or 4096x4096 per canvas).
  // Downscale the card on mobile to prevent an OOM crash.
  const isMobile = window.innerWidth <= 768;
  const W = isMobile ? 420 : 560;
  const H = isMobile ? 615 : 820;
  const off = document.createElement('canvas');
  off.width = W; off.height = H;
  const ctx = off.getContext('2d');
  if (!ctx) return null; // getContext returns null if canvas is already too large

  /* ─── helper: rounded rect path ─── */
  function rr(x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y,     x + w, y + r,     r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x,     y + h, x,     y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x,     y,     x + r, y,         r);
    ctx.closePath();
  }

  /* ─── clip to card shape ─── */
  rr(0, 0, W, H, 32);
  ctx.save();
  ctx.clip();

  /* ─── 1. white base ─── */
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, H);

  /* ─── 2. brand rainbow stripe (top) ─── */
  const rainbowGrad = ctx.createLinearGradient(0, 0, W, 0);
  rainbowGrad.addColorStop(0,    '#DF654D');
  rainbowGrad.addColorStop(0.28, '#F0D25B');
  rainbowGrad.addColorStop(0.58, '#78BE95');
  rainbowGrad.addColorStop(1,    '#4489C6');
  ctx.fillStyle = rainbowGrad;
  ctx.fillRect(0, 0, W, 7);

  /* ─── 3. navy header ─── */
  const HH = 222;
  const hGrad = ctx.createLinearGradient(0, 7, 0, HH);
  hGrad.addColorStop(0, '#ffffff');
  hGrad.addColorStop(1, '#ffffff');
  ctx.fillStyle = hGrad;
  ctx.fillRect(0, 7, W, HH - 7);

  /* subtle decorative circles in header */
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  ctx.beginPath(); ctx.arc(W - 40, 20, 145, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(50, HH + 15, 92, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = 'rgba(240,210,91,0.10)';
  ctx.beginPath(); ctx.arc(W + 5, HH - 5, 88, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = 'rgba(68,137,198,0.25)';
  ctx.beginPath(); ctx.arc(30, 30, 55, 0, Math.PI * 2); ctx.fill();

  /* ─── 4. white logo ─── */
  const logo = new Image();
  logo.src = logoUrl;
  await new Promise(res => { logo.onload = res; logo.onerror = res; });
  if (logo.naturalWidth > 0) {
    const lW = 232;
    const lH = Math.round((logo.naturalHeight / logo.naturalWidth) * lW);
    const lX = Math.round((W - lW) / 2);
    const lY = 7 + Math.round((HH - 7 - lH) / 2) - 14;
    ctx.drawImage(logo, lX, lY, lW, lH);
    const tmp = document.createElement('canvas');
    tmp.width = lW; tmp.height = lH;
    const tCtx = tmp.getContext('2d');
    tCtx.fillStyle = '#ffffff';
    tCtx.fillRect(0, 0, lW, lH);
  }

  /* ─── 5. "KARTU ABSENSI" gold badge in header ─── */
  // const bW = 162, bHt = 27, bX = (W - bW) / 2, bYbadge = HH - 44;
  // rr(bX, bYbadge, bW, bHt, bHt / 2);
  // ctx.fillStyle = 'rgba(240,210,91,0.18)'; ctx.fill();
  // ctx.strokeStyle = 'rgba(240,210,91,0.50)'; ctx.lineWidth = 1;
  // rr(bX, bYbadge, bW, bHt, bHt / 2); ctx.stroke();
  // ctx.fillStyle = '#F0D25B';
  // ctx.font = 'bold 10px Arial, sans-serif';
  // ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  // ctx.fillText('\u2726  KARTU ABSENSI  \u2726', W / 2, bYbadge + bHt / 2);

  /* ─── 6. dot-perforation divider (ticket tear line) ─── */
  const perfY = HH;
  ctx.fillStyle = '#e8eef8';
  for (let x = 12; x < W; x += 20) {
    ctx.beginPath(); ctx.arc(x, perfY, 5, 0, Math.PI * 2); ctx.fill();
  }

  /* ─── 7. light body background ─── */
  ctx.fillStyle = '#f4f8ff';
  ctx.fillRect(0, HH + 6, W, H - HH - 6);

  const bodyTop = HH + 18;

  /* ─── 8. participant name block ─── */
  ctx.fillStyle = '#8fa8c4';
  ctx.font = 'bold 10px Arial, sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillText('P E S E R T A', W / 2, bodyTop + 22);

  const rawName = props.fullName || props.results?.student?.name || props.nickname || 'Peserta';
  const displayName = rawName.length > 30 ? rawName.slice(0, 28) + '\u2026' : rawName;
  ctx.fillStyle = '#1a2f5e';
  ctx.font = 'bold 27px Arial, sans-serif';
  ctx.fillText(displayName, W / 2, bodyTop + 58);

  /* ─── 9. QR code card ─── */
  const qrSize = 216;
  const qrPad  = 18;
  const qrCW   = qrSize + qrPad * 2;
  const qrCH   = qrSize + qrPad * 2;
  const qrCX   = Math.round((W - qrCW) / 2);
  const qrCY   = bodyTop + 74;

  /* soft shadow */
  ctx.fillStyle = 'rgba(38,72,138,0.07)';
  rr(qrCX + 3, qrCY + 6, qrCW, qrCH, 20); ctx.fill();

  /* white card */
  ctx.fillStyle = '#ffffff';
  rr(qrCX, qrCY, qrCW, qrCH, 20); ctx.fill();
  ctx.strokeStyle = '#d8e8f7'; ctx.lineWidth = 1.5;
  rr(qrCX, qrCY, qrCW, qrCH, 20); ctx.stroke();

  /* QR image */
  ctx.drawImage(srcCanvas, qrCX + qrPad, qrCY + qrPad, qrSize, qrSize);

  /* corner bracket decorations */
  const bi = 9, bl = 24, bt = 3.5;
  const cx0 = qrCX + bi, cy0 = qrCY + bi;
  const cx1 = qrCX + qrCW - bi, cy1 = qrCY + qrCH - bi;
  ctx.strokeStyle = '#3265b0'; ctx.lineWidth = bt; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(cx0,      cy0 + bl); ctx.lineTo(cx0, cy0);      ctx.lineTo(cx0 + bl, cy0);      ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx1 - bl, cy0);      ctx.lineTo(cx1, cy0);      ctx.lineTo(cx1,      cy0 + bl); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx0,      cy1 - bl); ctx.lineTo(cx0, cy1);      ctx.lineTo(cx0 + bl, cy1);      ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx1 - bl, cy1);      ctx.lineTo(cx1, cy1);      ctx.lineTo(cx1,      cy1 - bl); ctx.stroke();

  /* ─── 10. barcode number pill ─── */
  const codeY = qrCY + qrCH + 18;
  const codeW = 214, codeH = 38, codeX = Math.round((W - codeW) / 2);
  rr(codeX, codeY, codeW, codeH, codeH / 2);
  ctx.fillStyle = '#EFF6FF'; ctx.fill();
  ctx.strokeStyle = '#cce0f5'; ctx.lineWidth = 1.5;
  rr(codeX, codeY, codeW, codeH, codeH / 2); ctx.stroke();
  ctx.fillStyle = '#3265b0';
  ctx.font = 'bold 15px "Courier New", monospace';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(props.barcodeCode || '', W / 2, codeY + codeH / 2);
  ctx.textBaseline = 'alphabetic';

  /* ─── 11. instruction note ─── */
  const noteY = codeY + codeH + 26;
  ctx.strokeStyle = '#dde9f8'; ctx.lineWidth = 1;
  ctx.setLineDash([5, 6]);
  ctx.beginPath(); ctx.moveTo(52, noteY); ctx.lineTo(W - 52, noteY); ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = '#9eb8d2';
  ctx.font = '12px Arial, sans-serif';
  ctx.textAlign = 'center';
  // ctx.fillText('Tunjukkan kepada panitia saat check-in', W / 2, noteY + 23);

  /* ─── 12. gold rule ─── */
  const goldY = H - 55;
  rr(40, goldY, W - 80, 5, 2.5);
  ctx.fillStyle = '#F0D25B'; ctx.fill();

  /* ─── 13. navy footer ─── */
  ctx.fillStyle = '#26488A';
  ctx.fillRect(0, H - 42, W, 42);
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = '11px Arial, sans-serif';
  ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
  ctx.fillText('Universitas Kristen Petra \u2014 Surabaya', W / 2, H - 21);

  ctx.restore();

  /* ─── trigger download ─── */
  return off;
}

async function previewQR() {
  isPreviewLoading.value = true;
  showPreview.value = true;
  const card = await buildCard();
  if (card) {
    previewDataUrl.value = card.toDataURL('image/png');
    _lastCardDataUrl = previewDataUrl.value;
  }
  isPreviewLoading.value = false;
}

function downloadFromPreview() {
  const safeName = (props.nickname || 'peserta').replace(/\s+/g, '-').toLowerCase();
  const link = document.createElement('a');
  link.download = `petra-parade-2026-${safeName}.png`;
  link.href = _lastCardDataUrl;
  link.click();
  showPreview.value = false;
}

async function downloadQR() {
  const card = await buildCard();
  if (!card) return;
  const safeName = (props.nickname || 'peserta').replace(/\s+/g, '-').toLowerCase();
  const link = document.createElement('a');
  link.download = `petra-parade-2026-${safeName}.png`;
  link.href = card.toDataURL('image/png');
  link.click();
}
</script>

<style scoped>
.barcode-card {
  background: #ffffff;
  border-radius: 28px;
  padding: 2.5rem;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 15px 50px rgba(38, 72, 138, 0.14);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(68, 137, 198, 0.15);
  font-family: 'Epilogue', sans-serif;
  animation: card-in 0.5s cubic-bezier(0.25, 0.8, 0.25, 1) both;
}

@keyframes card-in {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Corner decorations */
.deco {
  position: absolute;
  pointer-events: none;
  z-index: 0;
  width: 140px;
  height: 140px;
}
.deco--tl { top: -35px; left: -35px; }
.deco--br { bottom: -35px; right: -35px; }

.card-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* ---- Abby Header ---- */
.abby-header {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 12px;
}

.abby-mascot {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.abby-img {
  width: 120px;
  object-fit: contain;
  animation: float-abby 3s ease-in-out infinite;
  -webkit-mask-image: linear-gradient(to bottom, black 88%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 88%, transparent 100%);
  display: block;
}

@keyframes float-abby {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-6px); }
}

.abby-dialog {
  background: #EFF6FF;
  border: 2px solid #26488A;
  border-radius: 16px 16px 16px 0;
  padding: 0.85rem 1rem;
  max-width: 290px;
  text-align: left;
  font-size: 0.82rem;
  line-height: 1.55;
  color: #1e3a6e;
  box-shadow: 4px 4px 0 #26488A;
  animation: pop-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s both;
  position: relative;
}

.abby-dialog::before {
  content: '';
  position: absolute;
  bottom: -18px;
  left: -2px;
  border: 8px solid transparent;
  border-top-color: #26488A;
  border-bottom: none;
  border-left: none;
}

.abby-dialog::after {
  content: '';
  position: absolute;
  bottom: -13px;
  left: 0;
  border: 7px solid transparent;
  border-top-color: #EFF6FF;
  border-bottom: none;
  border-left: none;
}

@keyframes pop-in {
  from { opacity: 0; transform: scale(0.85) translateY(8px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

.greeting-title {
  font-weight: 800;
  font-size: 0.92rem;
  margin: 0 0 0.35rem;
  color: #26488A;
}

.greeting-body {
  margin: 0;
  font-size: 0.8rem;
}

/* ---- QR Section ---- */
.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.1rem;
}

.qr-wrapper {
  background: #f8fbff;
  border: 2px solid #e0ecf8;
  border-radius: 20px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  box-shadow: 0 4px 16px rgba(38, 72, 138, 0.08);
}

.qr-canvas {
  display: block;
  border-radius: 8px;
}

.qr-code-number {
  font-family: 'Epilogue', monospace;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: #4489C6;
  background: #EFF6FF;
  border: 1.5px solid #cce0f5;
  border-radius: 8px;
  padding: 0.3rem 0.85rem;
  user-select: all;
}

.qr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  color: #7d95b0;
  font-size: 0.8rem;
}

.qr-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(38, 72, 138, 0.15);
  border-top-color: #26488A;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ---- Instructions ---- */
.instructions-box {
  background: linear-gradient(135deg, #f0f7ff, #f8fbff);
  border: 1.5px solid #d0e8f8;
  border-radius: 16px;
  padding: 1rem 1.2rem;
  width: 100%;
}

.instructions-title {
  font-weight: 800;
  font-size: 0.82rem;
  color: #26488A;
  margin: 0 0 0.6rem;
}

.instructions-list {
  margin: 0;
  padding-left: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.instructions-list li {
  font-size: 0.8rem;
  color: #2c4a6e;
  line-height: 1.5;
}

/* ---- Button Row ---- */
.btn-row {
  display: flex;
  gap: 0.6rem;
  width: 100%;
}

/* ---- Preview Button ---- */
.btn-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex: 0 0 auto;
  padding: 1rem 1.1rem;
  border-radius: 16px;
  border: 2px solid #26488A;
  background: transparent;
  color: #26488A;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.btn-preview:hover {
  background: rgba(38, 72, 138, 0.08);
  transform: translateY(-2px);
}

.btn-preview:active {
  transform: scale(0.97);
}

/* ---- Download Button ---- */
.btn-download {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  flex: 1;
  padding: 1rem;
  border-radius: 16px;
  border: none;
  background: linear-gradient(135deg, #26488A, #1e3a6e);
  color: #ffffff;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(38, 72, 138, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.btn-download:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(38, 72, 138, 0.45);
}

.btn-download:active {
  transform: scale(0.98);
}

/* ---- Preview Modal ---- */
.preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.preview-modal {
  background: #fff;
  border-radius: 20px;
  width: min(480px, 100%);
  max-height: 92dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
}

.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e8edf4;
  flex-shrink: 0;
}

.preview-title {
  font-weight: 700;
  font-size: 1rem;
  color: #26488A;
}

.preview-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: #f0f4f9;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
  padding: 0;
}

.preview-close:hover {
  background: #dde4ef;
}

.close-icon {
  width: 14px;
  height: 14px;
  fill: #5a6a82;
}

.preview-body {
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  background: #f4f8ff;
}

.preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  color: #5a6a82;
  font-size: 0.9rem;
  padding: 2.5rem;
}

.preview-img {
  width: 100%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  display: block;
}

.preview-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid #e8edf4;
  flex-shrink: 0;
}

.preview-btn-download {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #26488A, #1e3a6e);
  color: #fff;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(38, 72, 138, 0.3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.preview-btn-download:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 26px rgba(38, 72, 138, 0.4);
}

/* ---- Modal transition ---- */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.22s ease;
}

.modal-fade-enter-active .preview-modal,
.modal-fade-leave-active .preview-modal {
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .preview-modal,
.modal-fade-leave-to .preview-modal {
  transform: scale(0.95);
  opacity: 0;
}

.btn-icon {
  width: 18px;
  height: 18px;
  fill: currentColor;
  flex-shrink: 0;
}

/* ---- Closing text ---- */
.event-closing {
  text-align: center;
  font-size: 0.8rem;
  color: #7d95b0;
  margin: 0;
}

/* ---- Responsive ---- */
@media (max-width: 480px) {
  .barcode-card {
    padding: 1.75rem 1.25rem;
  }

  .abby-img {
    width: 90px;
  }

  .abby-dialog {
    max-width: 220px;
  }
}

/* ---- View Tabs ---- */
.view-tabs {
  display: flex;
  gap: 0.5rem;
  background: #f0f4fa;
  border-radius: 14px;
  padding: 4px;
}

.tab-btn {
  flex: 1;
  padding: 0.55rem 0.5rem;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  color: #7d95b0;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  white-space: nowrap;
}

.tab-btn--active {
  background: #ffffff;
  color: #26488A;
  box-shadow: 0 2px 8px rgba(38, 72, 138, 0.12);
}

/* ---- Summary Section ---- */
.summary-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-block {
  background: #f8fafc;
  border-radius: 16px;
  padding: 1rem 1.1rem;
}

.summary-block-title {
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #7d95b0;
  margin: 0 0 0.7rem;
}

.chips-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.85rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.chip--petra {
  background: #EFF6FF;
  border: 1.5px solid #26488A;
  color: #1e3a6e;
}

.chip--custom {
  background: #fff9ec;
  border: 1.5px solid #F0D25B;
  color: #7a5200;
}

/* ---- Result Cards (light theme) ---- */
.result-card {
  border-radius: 20px;
  padding: 1.1rem 1.25rem;
}

.result-card-label {
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin: 0 0 0.75rem;
}

/* Priority card — warm gold */
.result-card--priority {
  background: linear-gradient(135deg, #fffdf4, #fff8e0);
  border: 1.5px solid #f0d25b;
}

.result-card--priority .result-card-label {
  color: #a07c10;
}

.priority-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 7px;
}

.priority-icon-wrap {
  width: 36px;
  height: 36px;
  background: #F0D25B;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.priority-star {
  width: 18px;
  height: 18px;
  fill: #5a3d00;
}

.priority-name {
  font-weight: 800;
  font-size: 0.95rem;
  color: #1e3a6e;
  flex: 1;
  line-height: 1.3;
}

.priority-badge {
  font-size: 0.68rem;
  font-weight: 700;
  background: #F0D25B;
  color: #5a3d00;
  padding: 0.22rem 0.65rem;
  border-radius: 20px;
  white-space: nowrap;
}

/* Chosen program card — green confirmation */
.result-card--chosen {
  background: linear-gradient(135deg, #f0faf4, #e6f7ec);
  border: 1.5px solid #78BE95;
}

.result-card--chosen .result-card-label {
  color: #2e7d52;
}

.chosen-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.chosen-icon-wrap {
  width: 36px;
  height: 36px;
  background: #78BE95;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chosen-icon {
  width: 18px;
  height: 18px;
  fill: #ffffff;
}

.chosen-name {
  font-weight: 800;
  font-size: 0.95rem;
  color: #1a4d30;
  flex: 1;
  line-height: 1.3;
}

.chosen-badge {
  font-size: 0.68rem;
  font-weight: 700;
  background: #78BE95;
  color: #1a4d30;
  padding: 0.22rem 0.65rem;
  border-radius: 20px;
  white-space: nowrap;
}

/* ---- Back to QR button ---- */
.btn-back-qr {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.9rem;
  border-radius: 16px;
  border: 2px solid #26488A;
  background: transparent;
  color: #26488A;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, transform 0.15s;
}

.btn-back-qr:hover {
  background: #26488A;
  color: #ffffff;
  transform: translateY(-1px);
}

.btn-back-qr:active {
  transform: scale(0.98);
}
</style>
